import React from "react";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

import type { ContentCardData } from "../../@types/portalAluno/contentCard";
import FastImage from "../FastImage/FastImage";
import type { ContentTopic } from "../../@types/portalAluno/topic";
import type { ContentDetailsTab } from "../../@types/portalAluno/contentDetails";
import "./styles.css";

const accessedAtFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

interface ContentCardProps {
  content: ContentCardData;
  saved?: boolean;
  priority?: boolean;
  onDetailsClick?: (content: ContentCardData, tab?: ContentDetailsTab) => void;
}

const ContentCard = ({
  content,
  saved = false,
  priority = false,
  onDetailsClick,
}: ContentCardProps) => {
  const hasModuleMetadata = typeof content.modules === "number";
  const moduleLabel = content.modules === 1 ? "Módulo" : "Módulos";
  const hasMetadata = hasModuleMetadata || Boolean(content.duration);
  const metadata = hasModuleMetadata
    ? `${content.modules} ${moduleLabel}${
        content.duration ? ` - ${content.duration}` : ""
      }`
    : content.duration;
  const accessedAt = React.useMemo(
    () =>
      content.accessedAt
        ? accessedAtFormatter.format(new Date(content.accessedAt))
        : undefined,
    [content.accessedAt]
  );
  const handleOpenDetails = React.useCallback(
    (tab?: ContentDetailsTab) => onDetailsClick?.(content, tab),
    [content, onDetailsClick]
  );

  return (
    <article
      className={`content-card${
        onDetailsClick ? " content-card--interactive" : ""
      }${content.unavailable ? " content-card--unavailable" : ""}`}
      onClick={() => handleOpenDetails()}
      onKeyDown={(event) => {
        if (
          onDetailsClick &&
          event.target === event.currentTarget &&
          (event.key === "Enter" || event.key === " ")
        ) {
          event.preventDefault();
          handleOpenDetails();
        }
      }}
      role={onDetailsClick ? "link" : undefined}
      tabIndex={onDetailsClick ? 0 : undefined}
      aria-label={onDetailsClick ? `Abrir detalhes de ${content.title}` : undefined}
    >
      <div className="content-card__image">
        <FastImage
          className="content-card__cover"
          src={content.imageUrl}
          alt={content.imageAlt}
          priority={priority}
        />

        {saved && (
          <span className="content-card__saved-flag" aria-label="Conteúdo salvo">
            <BookmarkRoundedIcon aria-hidden="true" />
          </span>
        )}
        {content.completed && !saved && (
          <span
            className="content-card__completed-flag"
            aria-label="Conteúdo concluído"
          >
            <CheckCircleRoundedIcon aria-hidden="true" />
          </span>
        )}
      </div>

      <div className="content-card__heading">
        <h3>{content.title}</h3>
        {typeof content.rating === "number" &&
          (onDetailsClick ? (
            <button
              type="button"
              className="content-card__rating content-card__rating--interactive"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenDetails("reviews");
              }}
              aria-label={`Ver feedbacks de ${content.title}. Nota ${content.rating}`}
            >
              <StarRoundedIcon aria-hidden="true" />
              <span>{content.rating.toFixed(1)}</span>
            </button>
          ) : (
            <span
              className="content-card__rating"
              aria-label={`Nota ${content.rating}`}
            >
              <StarRoundedIcon aria-hidden="true" />
              <span>{content.rating.toFixed(1)}</span>
            </span>
          ))}
      </div>

      <div className="content-card__footer">
        {(content.unavailable || accessedAt || hasMetadata) && (
          <div className="content-card__metadata-list">
            {content.unavailable && (
              <span className="content-card__unavailable">Expirado</span>
            )}
            {hasMetadata && (
              <span className="content-card__metadata">
                <MenuBookRoundedIcon aria-hidden="true" />
                <span>{metadata}</span>
              </span>
            )}
            {accessedAt && (
              <span className="content-card__metadata">
                <CalendarMonthRoundedIcon aria-hidden="true" />
                <span>Acessado em {accessedAt}</span>
              </span>
            )}
          </div>
        )}

        {onDetailsClick && (
          <button
            type="button"
            className="content-card__details"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenDetails("about");
            }}
            aria-label={`Ver detalhes de ${content.title}`}
          >
            Ver Detalhes
          </button>
        )}
      </div>
    </article>
  );
};

export default React.memo(ContentCard);

export type { ContentCardData, ContentTopic };
